<script setup>
import { onMounted, ref, watch, onUnmounted } from "vue";
import LogoHeaderWhite from '@/assets/images/svg/logo-header-white.svg';
import LogoHeaderColor from '@/assets/images/svg/logo-header-color.svg';

const NAV_HEIGHT = 80;

const isNavbarTop = ref(true);
const lastScrollTop = ref(0);
const isDownDirection = ref(false);
const isMobileMenu = ref(false);

let bodyElement = null;
let navElement = null;

const handleNavbarBackground = (currentScrollTop) => {
  if (!navElement) return;

  if (currentScrollTop >= NAV_HEIGHT && isNavbarTop.value) {
    isNavbarTop.value = false;
    navElement.classList.add('navbar-bg');
  } else if (currentScrollTop < NAV_HEIGHT && !isNavbarTop.value) {
    isNavbarTop.value = true;
    navElement.classList.remove('navbar-bg');
  }
};

const handleScrollDirection = (currentScrollTop) => {
  if (currentScrollTop > lastScrollTop.value + 40) {
    isDownDirection.value = true
  } else if (currentScrollTop < lastScrollTop.value) {
    isDownDirection.value = false;
  }

  lastScrollTop.value = currentScrollTop;
};

const handleScroll = () => {
  const currentScrollTop = window.pageYOffset || document.documentElement.scrollTop;

  handleNavbarBackground(currentScrollTop);
  handleScrollDirection(currentScrollTop);
};

const menuText = await queryContent('/menu').findOne();
const navItems = menuText.navItems;
const btnNavText = menuText.navButton;


onMounted(() => {
  navElement = document.getElementById('main-navbar');

  if (!navElement) return;

  handleScroll();
  window.addEventListener('scroll', handleScroll);
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});

const toggleMobileMenu = (isOpen) => {
  isMobileMenu.value = isOpen;
};

watch(() => isMobileMenu.value, (isOpen) => {
  if (!bodyElement) return;

  bodyElement.classList.toggle('with-modal', isOpen);
});
</script>

<template>
  <header :class="`header ${isDownDirection ? 'scrollDown' : 'scrollUp'}`">
    <div id="main-navbar" class="navbar navbar-fixed-top">
      <div class="container">
        <div class="header__content">
          <a href="#" class="header__logo" aria-label="Логотип компании">
            <LogoHeaderWhite class="logo" />
            <LogoHeaderColor class="logo-bg" />
          </a>
          <nav class="header__nav">
            <ul class="nav navbar-nav">
              <li class="nav__item">
                <a v-for="(item, index) in navItems" :key="index" :href="`#${item.link}`" class="nav__link text-18">
                  {{ item.title }}
                </a>
              </li>
            </ul>
            <a href="#reserve-section" class="nav__button btn btn-outline-primary">{{ btnNavText }}</a>
          </nav>
          <button class="navbar-open" type="button" @click="toggleMobileMenu" />
        </div>
      </div>
    </div>
  </header>
  <MenuMobile v-if="isMobileMenu" @close-mobile-menu="toggleMobileMenu" />
</template>

<style lang="scss">
.scrollDown {
  .navbar {
    transform: translateY(-120%);
  }
}

.scrollUp {
  .navbar {
    transform: translateY(0%);
  }
}

.header {
  .navbar {
    &-fixed-top {
      position: fixed;
      z-index: 10;
      left: 0;
      right: 0;
      top: 0;
    }

    &-open {
      display: none;

      @media (max-width: $medium) {
        display: block;
      }
    }
  }

  // меню прозрачное

  &__content {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-top: 30px;
    padding-bottom: 30px;

    @media (max-width: $large) {
      padding-top: 18px;
      padding-bottom: 18px;
    }

    @media (max-width: $mobile) {
      padding: 12px 8px;
    }
  }

  &__logo {
    display: flex;
    align-items: center;
    width: 234px;
    height: fit-content;
    margin-left: 36px;

    @media (max-width: $large) {
      margin-left: 0;
      width: 223px;
    }

    @media (max-width: $medium) {
      width: 188px;
    }

    @media (max-width: $mobile) {
      width: 145px;
    }

    .logo {
      display: block;
    }

    .logo-bg {
      display: none;
    }
  }

  &__nav {
    display: flex;
    color: var(--dark-primary-color);

    @media (max-width: $medium) {
      flex: 1;
      justify-content: flex-end;
      padding-right: 15px;
    }
  }

  .nav {
    display: flex;
    padding-right: 22px;

    @media (max-width: $large) {
      padding-right: 12px;
    }

    @media (max-width: $medium) {
      display: none;
    }

    &__item {
      padding: 0;
    }

    &__link {
      color: var(--dark-primary-color);
      display: inline-block;
      font-size: 18px;
      padding: 17px 26px;

      @media (max-width: $large) {
        padding: 17px 13px;
        font-size: 16px;
        white-space: nowrap;
      }

      @media (max-width: $medium) {
        padding: 17px 18px;
      }
    }

    &__button {
      display: flex;
      align-items: center;
      justify-content: center;

      @media (max-width: $large) {
        height: 55px;
        line-height: 1.2;
        font-size: 16px;
        padding: 0 19px;
        letter-spacing: 0.9px;
      }

      @media (max-width: $medium) {
        height: 48px;
        line-height: 1.2;
      }

      @media (max-width: $mobile) {
        display: none;
      }
    }
  }

  .navbar-open {
    display: none;
    width: 32px;
    height: 32px;
    border: 0;
    background: url('assets/images/svg/icon-menu.svg') center no-repeat transparent;
    margin-left: 19px;

    @media (max-width: $medium) {
      display: block;
    }
  }

  // меню белое

  .navbar-bg {
    background-color: var(--white-color);
    border-radius: 0 0 30px 30px;
    box-shadow: 0 4px 24px rgba(29, 29, 29, .2);

    @include transition();

    @media (max-width: $medium) {
      border-radius: 0;
    }

    .header__logo {
      .logo-bg {
        display: block;
      }

      .logo {
        display: none;
      }
    }

    .nav__link {
      color: var(--text-color);
    }

    .nav__button {
      background-color: var(--primary-color);
      border-color: var(--primary-color);

      &:hover,
      &:active,
      &:focus {
        background-color: var(--success-color);
        border-color: var(--success-color);
      }
    }

    .navbar-open {
      background: url('assets/images/svg/icon-menu-dark.svg') center no-repeat transparent;
      margin-left: 19px;
    }
  }
}
</style>
